/**
 * The 6-Figure Readiness Score: every question, point, threshold and text.
 *
 * Source: Julian's "NOVA Diagnostic funnel" spec (18 September 2026). The spec
 * requires points, thresholds and texts to live in config, so the engine in
 * `./engine.ts` holds no numbers and no copy of its own.
 *
 * A stored result records the config version it was scored with. Changing a
 * question, a point or a threshold registers a new version instead of editing
 * this one, so a score a coach has seen is never restated.
 *
 * Aliases: readiness score, six-figure readiness, 6-figure readiness score,
 * readiness config, diagnostic questions
 */

/** The score's name wherever it is shown. */
export const READINESS_SCORE_NAME = "6-Figure Readiness Score";

export const READINESS_COMPONENT_IDS = [
  "positioning",
  "offer",
  "social_presence",
  "lead_generation",
  "conversion_mechanism",
  "proof",
] as const;
export type ReadinessComponentId = (typeof READINESS_COMPONENT_IDS)[number];

export const READINESS_STATUSES = ["working", "started", "missing"] as const;
export type ReadinessStatus = (typeof READINESS_STATUSES)[number];

export const READINESS_BAND_IDS = [
  "starting_out",
  "early_foundations",
  "half_there",
  "almost_there",
  "six_figure_ready",
] as const;
export type ReadinessBand = (typeof READINESS_BAND_IDS)[number];

export const READINESS_QUESTION_IDS = [
  "positioning_clarity",
  "positioning_answer",
  "offer_format",
  "offer_paid_clients",
  "social_profile",
  "social_inbound",
  "lead_conversations",
  "lead_posting",
  "lead_routine",
  "conversion_experience",
  "conversion_referral",
  "conversion_cold",
  "proof_available",
  "proof_seen",
  "presentations",
  "revenue",
] as const;
export type ReadinessQuestionId = (typeof READINESS_QUESTION_IDS)[number];

export interface ReadinessOption {
  id: string;
  label: string;
  points: number;
}

export interface ReadinessQuestion {
  id: ReadinessQuestionId;
  /** The component the points count toward; `null` for Q15 and Q16, which add to the total only. */
  component: ReadinessComponentId | null;
  prompt: string;
  /** Grey line under the question. */
  note: string | null;
  options: readonly ReadinessOption[];
}

export interface ReadinessComponentConfig {
  id: ReadinessComponentId;
  name: string;
  /** The plain line under the name, in her words. */
  line: string;
  /** How the component reads inside a sentence: "your offer", "lead generation". */
  phrase: string;
  /** "Now:" line per status. The Working line doubles as "Working looks like:". */
  status: Readonly<Record<ReadinessStatus, string>>;
  /** The line under "What's holding you back right now: …". */
  holdingBack: string;
}

export interface ReadinessConfig {
  version: string;
  componentMaxPoints: number;
  maxPoints: number;
  /** Inclusive lower bounds on a component's points. Below `startedMin` is Missing. */
  statusMin: Readonly<{ working: number; started: number }>;
  /** Inclusive lower bounds on the shown score, lowest first. */
  bands: readonly Readonly<{ id: ReadinessBand; name: string; min: number }>[];
  components: readonly ReadinessComponentConfig[];
  questions: readonly ReadinessQuestion[];
  /** "No paying clients yet" skips the questions that need clients; they count 0. */
  noPaidClients: Readonly<{
    question: ReadinessQuestionId;
    option: string;
    skips: readonly ReadinessQuestionId[];
  }>;
  /** Q15 counts in full only when this component is Working; otherwise halved, rounded down. */
  presentations: Readonly<{
    question: ReadinessQuestionId;
    fullWhenWorking: ReadinessComponentId;
    readyOption: string;
  }>;
  revenue: Readonly<{
    question: ReadinessQuestionId;
    /** "$100K or more": the results page leads with the strategy call, and the NOVA trial stays beside it. */
    pastSixFiguresOption: string;
    readyOptions: readonly string[];
    /** At $60K to $100K the trial offer adds the strategy call as a second option. */
    strategyCallOptions: readonly string[];
  }>;
  /** What an otherwise 6-figure-ready score shows when revenue or presentations lag. */
  capScore: number;
  /** Proof is only named as holding her back once every other component is Working. */
  holdingBackLastResort: ReadinessComponentId;
  texts: Readonly<{
    statusLabel: Readonly<Record<ReadinessStatus, string>>;
    holdingBackPrefix: string;
    capLine: string;
    /** The pill and headline for a version 1 result past $100K, which has no score. */
    pastSixFiguresBand: string;
    pastSixFiguresLine: string;
    allWorkingHeadline: string;
    allWorkingLine: string;
    progressionTitle: string;
    summary: string;
    countLabel: string;
    goalLabel: string;
    startWith: string;
  }>;
}

/**
 * The first version. Past $100K it gave no score, band or constraint, so its
 * stored results with "$100K or more" carry `score: null`.
 */
export const READINESS_CONFIG_V1 = {
  version: "1",
  componentMaxPoints: 15,
  maxPoints: 115,
  statusMin: { working: 12, started: 4 },
  bands: [
    { id: "starting_out", name: "Starting out", min: 0 },
    { id: "early_foundations", name: "Early foundations", min: 20 },
    { id: "half_there", name: "Half there", min: 40 },
    { id: "almost_there", name: "Almost there", min: 60 },
    { id: "six_figure_ready", name: "6-figure ready", min: 80 },
  ],
  components: [
    {
      id: "positioning",
      name: "Positioning",
      line: "Who you help, and with what",
      phrase: "positioning",
      status: {
        missing: "You haven't chosen who you help yet.",
        started:
          "You have a group in mind, but not yet the exact problem you solve for them.",
        working:
          "You can say who you help and with what, the same way every time.",
      },
      holdingBack:
        "Until you can say who you help in one sentence, nothing else you build will land.",
    },
    {
      id: "offer",
      name: "Offer",
      line: "What you sell, and at what price",
      phrase: "your offer",
      status: {
        missing: "No clear offer yet.",
        started:
          "You have an offer, but the price and the number of clients aren't both there yet.",
        working: "One clear offer, and people pay for it.",
      },
      holdingBack:
        "Without one clear offer and a set price, it's hard for anyone to say yes.",
    },
    {
      id: "social_presence",
      name: "Social presence",
      line: "Your profile, and what people find when they look you up",
      phrase: "your social presence",
      status: {
        missing: "There's not much about your coaching to find yet.",
        started:
          "People can find you, but your profile doesn't bring anyone in yet.",
        working: "People find your profile and get in touch because of it.",
      },
      holdingBack:
        "What people find when they look you up isn't bringing them to you yet.",
    },
    {
      id: "lead_generation",
      name: "Lead generation",
      line: "How you reach out and post to start new conversations",
      phrase: "lead generation",
      status: {
        missing: "You don't reach out or post yet.",
        started: "You reach out or post sometimes, without a fixed routine.",
        working:
          "You reach out every week, post regularly, and it runs as a routine.",
      },
      holdingBack:
        "New people only find you by chance right now. A weekly routine of reaching out and posting changes that.",
    },
    {
      id: "conversion_mechanism",
      name: "Conversion mechanism",
      line: "How you turn interest into clients",
      phrase: "your conversion mechanism",
      status: {
        missing:
          "You don't have a conversion mechanism yet. People go from a conversation straight to your offer.",
        started:
          "You have a step before the offer, but it doesn't turn interest into clients often yet.",
        working:
          "Your conversion mechanism works. People experience your coaching first, and it turns into clients.",
      },
      holdingBack:
        "Without a step where people experience your coaching first, most conversations don't turn into clients.",
    },
    {
      id: "proof",
      name: "Proof",
      line: "What you can show",
      phrase: "proof",
      status: {
        missing: "Nothing written down yet that shows your coaching works.",
        started:
          "Some proof exists, but few people see it before they talk to you.",
        working:
          "Written proof with names, and people mention it before they hire you.",
      },
      holdingBack:
        "Your system works. What's missing is the written proof that makes the next client an easy yes.",
    },
  ],
  questions: [
    {
      id: "positioning_clarity",
      component: "positioning",
      prompt: "How clearly can you say who you help?",
      note: null,
      options: [
        {
          id: "anyone",
          label: "I coach anyone, I don't have a specific niche",
          points: 0,
        },
        {
          id: "several_groups",
          label:
            "I have several target groups, depending on who I'm talking to",
          points: 3,
        },
        {
          id: "specific_group",
          label:
            "I have a specific group of people I coach, for example executives, and we work on whatever comes up",
          points: 5,
        },
        {
          id: "client_and_problem",
          label:
            "I can name my ideal client and what I help them with, for example executives suffering from burnout",
          points: 8,
        },
      ],
    },
    {
      id: "positioning_answer",
      component: "positioning",
      prompt:
        "When people ask who you coach, which of these is closest to your answer?",
      note: null,
      options: [
        {
          id: "depends",
          label: "It depends, I coach all kinds of people",
          points: 0,
        },
        {
          id: "broad_group",
          label: "A broad group, for example “leaders” or “entrepreneurs”",
          points: 3,
        },
        {
          id: "problem_group",
          label:
            "People with a certain problem, for example “managers who are overwhelmed”",
          points: 5,
        },
        {
          id: "person_and_problem",
          label:
            "A specific kind of person with a specific problem, for example “VPs who do everything themselves because they can't delegate”",
          points: 7,
        },
      ],
    },
    {
      id: "offer_format",
      component: "offer",
      prompt: "How do you sell your coaching?",
      note: "Count only clients who pay you directly, not through their employer or a coaching company.",
      options: [
        {
          id: "no_paid_clients",
          label: "I don't have clients who have paid me yet",
          points: 0,
        },
        {
          id: "per_session",
          label: "Per session, or it depends on the client",
          points: 2,
        },
        {
          id: "package_under_1500",
          label: "A package with a set price, under $1,500",
          points: 4,
        },
        {
          id: "package_1500_to_3000",
          label: "A package with a set price, $1,500 to $3,000",
          points: 6,
        },
        {
          id: "package_over_3000",
          label: "A package with a set price, more than $3,000",
          points: 8,
        },
      ],
    },
    {
      id: "offer_paid_clients",
      component: "offer",
      prompt: "How many clients paid you directly in the last 12 months?",
      note: null,
      options: [
        { id: "none", label: "Nobody", points: 0 },
        { id: "one_to_two", label: "1 or 2 clients", points: 3 },
        { id: "three_to_nine", label: "3 to 9 clients", points: 5 },
        { id: "ten_plus", label: "10 or more clients", points: 7 },
      ],
    },
    {
      id: "social_profile",
      component: "social_presence",
      prompt:
        "If someone looked you up on social media today, what would they find about your coaching?",
      note: null,
      options: [
        {
          id: "nothing",
          label: "Nothing, or a profile that's not up to date",
          points: 0,
        },
        {
          id: "profile_without_who",
          label: "A profile, but it doesn't say who I help",
          points: 3,
        },
        {
          id: "profile_with_who",
          label: "A profile that says who I help, but not what I offer",
          points: 5,
        },
        {
          id: "profile_with_who_and_offer",
          label: "A profile that says who I help and what I offer",
          points: 8,
        },
      ],
    },
    {
      id: "social_inbound",
      component: "social_presence",
      prompt:
        "In the last three months, how many people got in touch after finding you on social media?",
      note: null,
      options: [
        { id: "none", label: "Nobody", points: 0 },
        { id: "one", label: "1 person", points: 3 },
        { id: "two_to_five", label: "2 to 5 people", points: 5 },
        { id: "more_than_five", label: "More than 5 people", points: 7 },
      ],
    },
    {
      id: "lead_conversations",
      component: "lead_generation",
      prompt:
        "In a typical week, how many new people do you start a conversation with about your coaching?",
      note: null,
      options: [
        { id: "none", label: "Nobody", points: 0 },
        { id: "one_to_five", label: "1 to 5 people", points: 2 },
        { id: "six_to_twelve", label: "6 to 12 people", points: 3 },
        { id: "thirteen_to_twenty", label: "13 to 20 people", points: 5 },
        { id: "more_than_twenty", label: "More than 20 people", points: 6 },
      ],
    },
    {
      id: "lead_posting",
      component: "lead_generation",
      prompt: "How often do you post about your coaching or your expertise?",
      note: null,
      options: [
        { id: "never", label: "Never", points: 0 },
        { id: "now_and_then", label: "Now and then", points: 2 },
        { id: "most_weeks", label: "Most weeks", points: 3 },
        { id: "several_per_week", label: "Several times a week", points: 4 },
      ],
    },
    {
      id: "lead_routine",
      component: "lead_generation",
      prompt:
        "Do you have a fixed weekly routine for reaching out and posting?",
      note: null,
      options: [
        { id: "no", label: "No", points: 0 },
        {
          id: "not_always",
          label: "I have a routine, but I don't always follow it",
          points: 2,
        },
        { id: "every_week", label: "Yes, every week", points: 5 },
      ],
    },
    {
      id: "conversion_experience",
      component: "conversion_mechanism",
      prompt:
        "How do people get to see what your coaching is like before they hire you?",
      note: null,
      options: [
        {
          id: "straight_to_offer",
          label: "They don't, we just talk and I make my offer",
          points: 0,
        },
        {
          id: "free_session",
          label: "They get one free coaching session on their real problem",
          points: 2,
        },
        {
          id: "webinar_then_call",
          label: "They watch a webinar or a video, then book a call",
          points: 4,
        },
        {
          id: "challenge_or_low_price",
          label: "They join a free challenge or buy a low-priced product",
          points: 5,
        },
        {
          id: "free_days",
          label: "They get a few days of free coaching on their real problem",
          points: 8,
        },
      ],
    },
    {
      id: "conversion_referral",
      component: "conversion_mechanism",
      prompt:
        "Of the last five people who came to you through a referral, a friend or someone you know, how many became paying clients?",
      note: null,
      options: [
        {
          id: "fewer_than_five",
          label: "Haven't had 5 of those yet",
          points: 0,
        },
        { id: "none", label: "Nobody", points: 0 },
        { id: "one_to_two", label: "1 or 2 people", points: 1 },
        { id: "three_plus", label: "3 or more people", points: 2 },
      ],
    },
    {
      id: "conversion_cold",
      component: "conversion_mechanism",
      prompt:
        "Of the last five people who found you without a referral, how many became paying clients?",
      note: null,
      options: [
        {
          id: "fewer_than_five",
          label: "Haven't had 5 of those yet",
          points: 0,
        },
        { id: "none", label: "Nobody", points: 0 },
        { id: "one", label: "1 person", points: 3 },
        { id: "two_plus", label: "2 or more people", points: 5 },
      ],
    },
    {
      id: "proof_available",
      component: "proof",
      prompt:
        "If a potential client asked you today for proof that your coaching works, what could you show her?",
      note: null,
      options: [
        { id: "nothing", label: "Nothing yet", points: 0 },
        {
          id: "said_not_written",
          label: "People have said good things, but nothing is written down",
          points: 3,
        },
        {
          id: "one_to_four_testimonials",
          label: "1 to 4 written testimonials with names",
          points: 5,
        },
        {
          id: "testimonials_and_story",
          label:
            "5 or more written testimonials with names, and at least one full written client story with the situation before and after",
          points: 8,
        },
      ],
    },
    {
      id: "proof_seen",
      component: "proof",
      prompt:
        "Of the last five people who came to a conversation with you, how many had already seen a result of one of your clients?",
      note: null,
      options: [
        {
          id: "fewer_than_five",
          label: "Haven't talked to 5 people yet",
          points: 0,
        },
        { id: "none", label: "Nobody", points: 0 },
        { id: "one_to_two", label: "1 or 2 people", points: 5 },
        { id: "three_plus", label: "3 or more", points: 7 },
      ],
    },
    {
      id: "presentations",
      component: null,
      prompt:
        "In a typical month, how many people do you present your paid coaching offer and its price to?",
      note: null,
      options: [
        { id: "none", label: "Nobody", points: 0 },
        { id: "one_to_two", label: "1 or 2 people", points: 5 },
        { id: "three_to_nine", label: "3 to 9 people", points: 10 },
        { id: "ten_plus", label: "10 or more people", points: 15 },
      ],
    },
    {
      id: "revenue",
      component: null,
      prompt:
        "Roughly, how much did coaching bring in over the last 12 months?",
      note: "Count only coaching that clients paid you for directly, not through their employer or a coaching company.",
      options: [
        { id: "nothing", label: "Nothing yet", points: 0 },
        { id: "under_25k", label: "Under $25K", points: 3 },
        { id: "from_25k_to_60k", label: "$25K to $60K", points: 6 },
        { id: "from_60k_to_100k", label: "$60K to $100K", points: 10 },
        { id: "over_100k", label: "$100K or more", points: 0 },
      ],
    },
  ],
  noPaidClients: {
    question: "offer_format",
    option: "no_paid_clients",
    skips: ["offer_paid_clients", "proof_available", "proof_seen"],
  },
  presentations: {
    question: "presentations",
    fullWhenWorking: "conversion_mechanism",
    readyOption: "ten_plus",
  },
  revenue: {
    question: "revenue",
    pastSixFiguresOption: "over_100k",
    readyOptions: ["from_60k_to_100k", "over_100k"],
    strategyCallOptions: ["from_60k_to_100k"],
  },
  capScore: 79,
  holdingBackLastResort: "proof",
  texts: {
    statusLabel: { working: "Working", started: "Started", missing: "Missing" },
    holdingBackPrefix: "What's holding you back right now:",
    capLine:
      "Your answers say the system is in place. The numbers should follow, and that's what to look at first.",
    pastSixFiguresBand: "You're past $100K",
    pastSixFiguresLine:
      "A readiness score for $100K doesn't tell you much. Here is how your system looks, and what to do next.",
    allWorkingHeadline: "All six are working",
    allWorkingLine:
      "Your system is in place. What moves you now is running it every week.",
    progressionTitle: "Your progression to 6 figures",
    summary:
      "A 6-figure coaching business needs all six working. You have {working} working, {started} started and {missing} missing.",
    countLabel: "{working} of 6 working",
    goalLabel: "All 6 working = a 6-figure system",
    startWith: "Start with {phrase}, today.",
  },
} as const satisfies ReadinessConfig;

/**
 * Every coach gets a score, by Yuri's decision: "$100K or more" scores
 * like "$60K to $100K", the top revenue band. Nothing else changes.
 */
export const READINESS_CONFIG_V2 = {
  ...READINESS_CONFIG_V1,
  version: "2",
  questions: READINESS_CONFIG_V1.questions.map((question) =>
    question.id === "revenue"
      ? {
          ...question,
          options: question.options.map((option) =>
            option.id === "over_100k" ? { ...option, points: 10 } : option,
          ),
        }
      : question,
  ),
} satisfies ReadinessConfig;

/**
 * "Directly" kept executive coaches paid by client companies from counting
 * those clients, so they answered "no paying clients" and scored 0. The
 * offer and revenue notes and the paid-clients question now count clients who
 * pay the coach or the coach's business for their own coaching. Points and
 * thresholds are version 2's.
 */
export const READINESS_CONFIG_V3 = {
  ...READINESS_CONFIG_V2,
  version: "3",
  questions: READINESS_CONFIG_V2.questions.map((question) => {
    switch (question.id) {
      case "offer_format":
        return {
          ...question,
          note: "Count clients who pay you or your business for your own coaching, including when their company pays. Don't count coaching you do as an employee or contractor of a coaching company.",
        };
      case "offer_paid_clients":
        return {
          ...question,
          prompt:
            "How many clients paid you or your business for coaching in the last 12 months?",
        };
      case "revenue":
        return {
          ...question,
          note: "Count what you or your business earned from your own coaching, including payments from clients' companies. Don't count pay from a coaching company you work for.",
        };
      default:
        return question;
    }
  }),
} satisfies ReadinessConfig;

export const CURRENT_READINESS_CONFIG: ReadinessConfig = READINESS_CONFIG_V3;

const READINESS_CONFIGS: Readonly<Record<string, ReadinessConfig>> = {
  [READINESS_CONFIG_V1.version]: READINESS_CONFIG_V1,
  [READINESS_CONFIG_V2.version]: READINESS_CONFIG_V2,
  [READINESS_CONFIG_V3.version]: READINESS_CONFIG_V3,
};

export class UnknownReadinessConfigVersion extends Error {
  constructor(version: string) {
    super(`No 6-Figure Readiness Score config is registered for "${version}"`);
    this.name = "UnknownReadinessConfigVersion";
  }
}

/** The config a stored result was scored with. */
export function readinessConfigFor(version: string): ReadinessConfig {
  const config = READINESS_CONFIGS[version];
  if (!config) throw new UnknownReadinessConfigVersion(version);
  return config;
}

export function isReadinessConfigVersion(version: string): boolean {
  return version in READINESS_CONFIGS;
}
